/**
 * Release 2 S3 (A) — create a smart board or edit its definition. The predicate builder is the board filter bar
 * (`FilterBar`, which reuses `features/search/predicates/*`) plus the two things a board bar has no control for:
 * the watched boards (scope) and the tidy predicates (`untagged` / `unfiled` / `incomplete`).
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Folder, Plus, X } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useBoard, useCreateSmartBoard, useLibraries, useUpdateSmartBoardDefinition } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input, Label, Select } from '@/components/Input';
import { isApiError } from '@/lib/api/errors';
import { FilterBar } from '@/features/grid/FilterBar';
import { BoardPickerDialog } from '@/features/boards/BoardPickerDialog';
import { DEFAULT_SMART_SORT, SMART_SORTS, TIDY_HINT, TIDY_KEYS, TIDY_LABEL, countDefinition, joinDefinition, splitDefinition, type SmartDraft, type SmartSort } from './definition';

export type SmartBoardDialogProps =
  | { mode: 'create'; initial: SmartDraft; libraryId?: string; note?: string; onClose: () => void }
  | { mode: 'edit'; board: Board; onClose: () => void };

export function SmartBoardDialog(props: SmartBoardDialogProps) {
  const { ws = '' } = useParams();
  const navigate = useNavigate();
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const libraries = useLibraries(wsId);
  const create = useCreateSmartBoard(wsId);
  const update = useUpdateSmartBoardDefinition();
  const editing = props.mode === 'edit' ? props.board : null;
  const [draft, setDraft] = useState<SmartDraft>(() => (props.mode === 'edit' ? splitDefinition(props.board.smart_filters) : props.initial));
  const [name, setName] = useState('');
  const [libraryId, setLibraryId] = useState(props.mode === 'create' ? (props.libraryId ?? '') : '');
  const [sort, setSort] = useState<SmartSort | ''>(editing?.smart_sort ?? '');
  const [picking, setPicking] = useState(false);
  const library = libraryId || libraries.data?.[0]?.id || '';
  const pending = create.isPending || update.isPending;
  const n = countDefinition(draft);

  const fail = (e: unknown) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not save the smart board' });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const filters = joinDefinition(draft);
    if (editing) {
      update.mutate({ boardId: editing.id, definition: { filters, sort: sort || null } }, { onSuccess: () => (toast({ kind: 'success', title: 'Definition saved' }), props.onClose()), onError: fail });
      return;
    }
    if (!name.trim() || !library) return;
    create.mutate(
      { library_id: library, name: name.trim(), filters, ...(sort ? { sort } : {}) },
      {
        onSuccess: (b) => {
          toast({ kind: 'success', title: `Smart board “${b.name}” created` });
          props.onClose();
          navigate(`/w/${ws}/b/${b.id}`);
        },
        onError: fail,
      },
    );
  };

  return (
    <Dialog
      open
      onClose={props.onClose}
      width="max-w-2xl"
      title={editing ? `Edit definition — ${editing.name}` : 'New smart board'}
      footer={
        <>
          <span className="mr-auto self-center text-xs text-fg-subtle" data-predicate-count>{n ? `${n} ${n === 1 ? 'condition' : 'conditions'}` : 'No conditions: every asset you can see'}</span>
          <Button onClick={props.onClose}>Cancel</Button>
          <Button variant="primary" form="smart-board" type="submit" disabled={pending || (!editing && (!name.trim() || !library))}>
            {editing ? 'Save definition' : 'Create smart board'}
          </Button>
        </>
      }
    >
      <form id="smart-board" onSubmit={submit} className="space-y-4" data-smart-board-dialog>
        {!editing && (
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <div>
              <Label htmlFor="smart-board-name">Name</Label>
              <Input id="smart-board-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
            </div>
            <div>
              <Label htmlFor="smart-board-library">Library</Label>
              <Select id="smart-board-library" value={library} onChange={(e) => setLibraryId(e.target.value)}>
                {libraries.data?.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </div>
          </div>
        )}
        {props.mode === 'create' && props.note && <p className="text-xs text-fg-muted">{props.note}</p>}

        <fieldset>
          <legend className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Watch</legend>
          <div className="flex flex-wrap items-center gap-1" data-smart-scope>
            {draft.scope.boardIds.length === 0 && <span className="text-xs text-fg-muted">All boards (each viewer sees only the boards they can open)</span>}
            {draft.scope.boardIds.map((id) => (
              <ScopeChip key={id} boardId={id} onRemove={() => setDraft((d) => ({ ...d, scope: { ...d.scope, boardIds: d.scope.boardIds.filter((x) => x !== id) } }))} />
            ))}
            <button type="button" className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2 py-0.5 text-xs hover:bg-bg-sunken" onClick={() => setPicking(true)}>
              <Plus size={11} /> board
            </button>
          </div>
          {draft.scope.boardIds.length > 0 && (
            <label className="mt-1 flex items-center gap-2 text-xs text-fg-muted">
              <input type="checkbox" checked={draft.scope.includeDescendants} onChange={(e) => setDraft((d) => ({ ...d, scope: { ...d.scope, includeDescendants: e.target.checked } }))} /> include sub-boards
            </label>
          )}
        </fieldset>

        <fieldset>
          <legend className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Tidy</legend>
          <div className="flex flex-wrap gap-3 text-xs">
            {TIDY_KEYS.map((k) => (
              <label key={k} className="flex items-center gap-1.5" title={TIDY_HINT[k]}>
                <input type="checkbox" checked={!!draft.tidy[k]} onChange={(e) => setDraft((d) => ({ ...d, tidy: { ...d.tidy, [k]: e.target.checked || undefined } }))} data-tidy={k} /> {TIDY_LABEL[k]}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Conditions</legend>
          <FilterBar filters={draft.rest} onChange={(rest) => setDraft((d) => ({ ...d, rest }))} facets={undefined} total={null} saveAsSmart={false} />
        </fieldset>

        <div className="flex items-center gap-2">
          <Label htmlFor="smart-board-sort" className="mb-0">Sort</Label>
          <Select id="smart-board-sort" value={sort} onChange={(e) => setSort(e.target.value as SmartSort | '')} className="h-8 w-auto text-xs">
            <option value="">Default ({SMART_SORTS.find((s) => s.id === DEFAULT_SMART_SORT)!.label.toLowerCase()})</option>
            {SMART_SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </Select>
        </div>
      </form>
      {picking && (
        <BoardPickerDialog
          title="Watch board"
          confirmLabel="Add"
          exclude={editing ? [editing.id] : []}
          onClose={() => setPicking(false)}
          onPick={({ boardId }) => {
            setPicking(false);
            if (boardId) setDraft((d) => ({ ...d, scope: { ...d.scope, boardIds: [...new Set([...d.scope.boardIds, boardId])] } }));
          }}
        />
      )}
    </Dialog>
  );
}

function ScopeChip({ boardId, onRemove }: { boardId: string; onRemove: () => void }) {
  const board = useBoard(boardId);
  const label = board.data?.name ?? (board.isError ? 'Board unavailable' : '…');
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent-soft px-2 py-0.5 text-xs text-accent" data-scope-board={boardId}>
      <Folder size={11} /> {label}
      <button type="button" aria-label={`Stop watching ${label}`} onClick={onRemove} className="rounded hover:text-danger"><X size={11} /></button>
    </span>
  );
}
